import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@matix/auth';
import {
  couponsAPI,
  discountsAPI,
  getApiErrorMessage,
  type CouponEngineResult,
} from '@matix/api-client';
import type {
  CouponValidationOk,
  CouponValidationResponse,
  DiscountReason,
} from '@matix/types';
import { useUI } from '../../store/ui';
import { roundMoney } from '@matix/utils';
import { useCart, useCartTotals } from '../../store/cart';
import { amountStep, amountText, moneyPrecision } from '../../store/currency';
import { useTaxConfig } from '../../store/taxConfig';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { OverrideModal } from './OverrideModal';
import { CheckIcon, TrashIcon } from '../../components/icons';
import { useCurrency } from '../print/useCurrency';

/**
 * Discount modal — header-level discount + coupon redemption.
 *
 * The modal coordinates three input modes that resolve to one
 * `Discount` object on the cart:
 *
 *   1. Manual percent — quick chips (25/100%) + free input,
 *      reason picker (mstr_DiscountReasons), optional manager override.
 *   2. Manual amount — quick chips (10/20/50/100/200 EGP) + free input,
 *      same reason picker, override based on the implied percent.
 *   3. Coupon code — typed/scanned, validated server-side. When
 *      applied, manual inputs are disabled (exclusive). Removing the
 *      coupon re-enables manual mode.
 *
 * Manager override:
 *   - The cashier's `permissions.max_receipt_discount_perc` from the
 *     JWT is the limit. If the requested discount (always normalized
 *     to percent for the check — amount-type discounts compute their
 *     implied percent against the cart subtotal) exceeds the limit,
 *     OR the chosen reason has `requires_manager_override`, the
 *     cashier must complete the OverrideModal child flow before Apply
 *     enables.
 *   - Override approval is per-modal-open. Closing and reopening the
 *     modal clears the approval so the cashier can't re-use it later.
 *
 * Live preview:
 *   - The "Subtotal / Discount / New total" block reflects the current
 *     entry state immediately. Coupon discount uses the server's
 *     `applies_to_amount` (no float drift); manual uses local math.
 */
export function DiscountModal() {
  const isOpen = useUI((s) => s.openModal === 'discount');
  const closeModal = useUI((s) => s.closeModal);
  const { cashier } = useAuth();
  const { t } = useLang();

  const customer = useCart((s) => s.customer);
  // The basket, so the coupon engine can enforce product/category rules.
  const lines = useCart((s) => s.lines);
  const setDiscount = useCart((s) => s.setDiscount);
  const existingDiscount = useCart((s) => s.discount);
  // VAT rate from tenant config (loaded once on app boot, see store/taxConfig.ts).
  const taxRate = useTaxConfig((s) => s.config.sales_percentage / 100);

  const totals = useCartTotals();
  // Pre-discount value the modal works with. A PURE return discounts the
  // refund (the server puts the header discount on the return leg then).
  // After the invoice-level discount a return from an invoice already carries.
  const subtotal = roundM(
    totals.mode === 'return' ? totals.returnSubtotal - totals.returnInheritedDiscount : totals.subtotal,
  );

  // ── Local state ──────────────────────────────────────────────────────

  /** Active mode toggle — disabled when a coupon is applied. Opens on Value
   *  (a fixed amount), not %, unless re-editing a percent discount. */
  const [mode, setMode] = useState<'percent' | 'amount'>('amount');

  /** Free text in the value field. We keep it as string so the cashier
   *  can clear it without it snapping back to "0". */
  const [valueStr, setValueStr] = useState<string>('');

  /** Selected reason from the dropdown. Required for manual discounts. */
  const [reasonId, setReasonId] = useState<number | null>(null);

  /** Coupon code input (always trimmed before sending). */
  const [couponInput, setCouponInput] = useState<string>('');

  /** The currently-applied coupon, or null. Set by handleApplyCoupon. */
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidationOk | null>(null);

  /**
   * A coupon accepted by the NEW engine (migration 038). Kept separate from
   * `appliedCoupon` (the legacy GL_Coupon_* result) because the two are booked
   * completely differently: this one travels to the server as a CODE and is
   * priced there, while the legacy one is applied as an ordinary discount
   * amount. Only one can be set at a time.
   */
  const [engineCoupon, setEngineCoupon] = useState<CouponEngineResult | null>(null);

  /** The most recent coupon failure (to render under the input). */
  const [couponError, setCouponError] = useState<string | null>(null);

  /** True while POST /coupons/validate is in flight. */
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  /**
   * If the user crosses their permission threshold and successfully
   * authorizes via OverrideModal, we keep the manager_user_id +
   * username here. Cleared when the modal reopens (per-session approval).
   */
  const [override, setOverride] = useState<{
    managerUserId: number;
    managerUsername: string;
  } | null>(null);

  /** Reset state on each open. Pre-fills if a discount is already on the cart. */
  useEffect(() => {
    if (!isOpen) return;
    if (existingDiscount) {
      // Open in "edit" mode preserving the existing discount.
      if (existingDiscount.source === 'coupon' && existingDiscount.couponCode) {
        // We don't re-fetch the coupon from the server — re-applying
        // the same code is fine if the cashier explicitly hits Apply.
        // For now, the safest UX is to show the coupon code in the input
        // and let them re-validate explicitly; the alternative (treating
        // the cart's discount as authoritative) means the modal could
        // disagree with the server.
        setMode('amount');
        setValueStr('');
        setReasonId(null);
        setCouponInput(existingDiscount.couponCode);
        setAppliedCoupon(null);
      } else {
        setMode(existingDiscount.type);
        setValueStr(String(existingDiscount.value));
        setReasonId(existingDiscount.discountReasonId ?? null);
        setCouponInput('');
        setAppliedCoupon(null);
      }
    } else {
      setMode('amount');
      setValueStr('');
      setReasonId(null);
      setCouponInput('');
      setAppliedCoupon(null);
    }
    setCouponError(null);
    setOverride(null);
    setValidatingCoupon(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Reasons dropdown — fetched once per session ─────────────────────
  const { data: reasonsData } = useQuery({
    queryKey: ['discount-reasons'],
    queryFn: () => discountsAPI.listReasons(),
    staleTime: Infinity, // tenant ops change rarely; load once
    enabled: isOpen,
  });
  const reasons: DiscountReason[] = reasonsData?.reasons ?? [];

  const currency = useCurrency();

  // ── Derived values ───────────────────────────────────────────────────
  const numericValue = parseFloat(valueStr);
  const valueIsValid = Number.isFinite(numericValue) && numericValue > 0;

  /** Discount amount in EGP, given the current entry state. */
  const liveDiscountAmount = (() => {
    if (engineCoupon) return engineCoupon.discount_amount;
    if (appliedCoupon) return appliedCoupon.applies_to_amount;
    if (!valueIsValid) return 0;
    if (mode === 'percent') {
      return roundM(Math.min(subtotal * (numericValue / 100), subtotal));
    }
    return roundM(Math.min(numericValue, subtotal));
  })();

  /** New total preview = subtotal − discount + delivery + VAT. */
  const liveNewTotal = (() => {
    const afterDiscount = subtotal - liveDiscountAmount;
    const vat = afterDiscount * taxRate;
    const deliveryFee = totals.deliveryFee;
    return roundM(afterDiscount + vat + deliveryFee);
  })();

  /**
   * The implied discount percent against the cart subtotal. Used for
   * the manager-override threshold check (which is always percent-based
   * even when the cashier entered an amount).
   */
  const impliedPerc = subtotal > 0 ? round2((liveDiscountAmount / subtotal) * 100) : 0;

  const cashierMaxPerc = cashier?.permissions?.max_receipt_discount_perc ?? 0;
  // Security_Users.ShowReceiptOverallDiscount — the "Allow invoice discount"
  // tick. Off = no manual invoice discount at all (no approval path, as in the
  // legacy till); a coupon still applies. A token from before this field read
  // as allowed — the server re-checks the column on every order.
  const manualAllowed = cashier?.permissions?.allow_invoice_discount !== false;

  /** The selected reason object, if any. */
  const selectedReason = reasons.find((r) => r.id === reasonId) ?? null;

  /**
   * Override needed if:
   *   - the cashier has zero discount permission, OR
   *   - implied percent exceeds the cashier's limit, OR
   *   - the chosen reason flags requires_manager_override.
   *
   * `max_receipt_discount_perc = 0` in Security_Users means the cashier
   * has NO discount authority at all — every discount value (even 1%)
   * triggers an override prompt. The check below covers this naturally:
   * any positive impliedPerc > 0 will exceed a max of 0.
   *
   * Coupons skip the override check — coupon discounts are pre-authorized
   * by tenant ops at coupon creation time.
   */
  const overrideNeeded = (() => {
    if (engineCoupon || appliedCoupon) return false;
    // Temporary (per ops request, 2026-06-06): a full 100% discount is
    // allowed through WITHOUT manager password verification. Skip the
    // override check entirely for full comps until a proper policy lands.
    if (impliedPerc >= 100) return false;
    if (selectedReason?.requires_manager_override) return true;
    return impliedPerc > cashierMaxPerc;
  })();

  /** Apply enabled when the discount is valid + override OK.
   *
   *  Per ops: a discount reason is NEVER mandatory — the cashier can apply any
   *  discount without picking a reason. The reason picker stays available so
   *  it can still be documented when desired, but it never gates Apply. Manager
   *  override (when required by the percent limit or a reason's own
   *  `requires_manager_override` flag) is unaffected by this.
   */
  const reasonRequired = false;
  const canApply = (() => {
    if (engineCoupon || appliedCoupon) return true; // coupon always applies
    if (!manualAllowed) return false;
    if (!valueIsValid) return false;
    if (reasonRequired && !selectedReason) return false;
    if (impliedPerc > 100) return false;           // sanity ceiling
    if (overrideNeeded && !override) return false;
    return true;
  })();

  // ── Coupon ──────────────────────────────────────────────────────────

  async function handleApplyCoupon() {
    const code = couponInput.trim();
    if (!code) {
      setCouponError(t({ en: 'Enter a coupon code first', ar: 'أدخل رمز القسيمة أولاً' }));
      return;
    }
    setValidatingCoupon(true);
    setCouponError(null);
    try {
      // The NEW engine first: it knows the basket, so it can enforce product,
      // category, branch, order-type and per-customer rules the legacy one
      // cannot. Only when this tenant has no coupon schema (migration 038 not
      // applied) do we fall back to the legacy validator, so nothing regresses
      // for a tenant still running the old coupons.
      const engine = await couponsAPI
        .validate({
          code,
          customerId: customer?.id ?? null,
          lines: lines
            .filter((l) => !l.isReturn)
            .map((l) => ({
              item_id: l.product.id,
              qty: l.qty,
              total: roundMoney(l.qty * l.unitPrice, moneyPrecision()),
            })),
        })
        .catch(() => null);

      if (engine && engine.reason !== 'COUPONS_NOT_AVAILABLE') {
        if (engine.valid) {
          setEngineCoupon(engine);
          setAppliedCoupon(null);
          setCouponError(null);
          setValueStr('');
          setReasonId(null);
          setOverride(null);
        } else {
          setEngineCoupon(null);
          setAppliedCoupon(null);
          setCouponError(engine.message ?? t({ en: 'This coupon cannot be used', ar: 'لا يمكن استخدام هذه القسيمة' }));
        }
        return;
      }

      const result: CouponValidationResponse = await discountsAPI.validateCoupon({
        code,
        subtotal,
        customerId: customer?.id ?? null,
      });
      if (result.valid) {
        setEngineCoupon(null);
        setAppliedCoupon(result);
        setCouponError(null);
        // Coupons are exclusive — clear any in-progress manual entry so
        // the cashier doesn't see stale state if they remove the coupon.
        setValueStr('');
        setReasonId(null);
        setOverride(null);
      } else {
        setAppliedCoupon(null);
        setCouponError(result.message);
      }
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(getApiErrorMessage(err) || t({ en: 'Could not validate coupon', ar: 'تعذّر التحقق من القسيمة' }));
    } finally {
      setValidatingCoupon(false);
    }
  }

  function handleRemoveCoupon() {
    setAppliedCoupon(null);
    setEngineCoupon(null);
    setCouponInput('');
    setCouponError(null);
  }

  // ── Override ────────────────────────────────────────────────────────

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);

  function handleRequestOverride() {
    setOverrideModalOpen(true);
  }
  function handleOverrideSuccess(args: { managerUserId: number; managerUsername: string }) {
    setOverride(args);
    setOverrideModalOpen(false);
    toast(t({ en: `Override approved by ${args.managerUsername}`, ar: `تمت الموافقة من ${args.managerUsername}` }), 'success');
  }
  function handleOverrideClose() {
    setOverrideModalOpen(false);
  }

  // ── Apply ───────────────────────────────────────────────────────────

  function handleApply() {
    // A coupon priced by the engine: store the SERVER's amount for display and
    // the cart's totals, and flag it so the order request sends the code rather
    // than the money. The server re-prices it at charge time.
    if (engineCoupon) {
      setDiscount({
        type: 'amount',
        value: engineCoupon.discount_amount,
        reason: engineCoupon.name ?? engineCoupon.code ?? couponInput.trim(),
        source: 'coupon',
        couponCode: engineCoupon.code ?? couponInput.trim(),
        serverPriced: true,
      });
      toast(
        t({
          en: `Coupon applied · ${fmt(engineCoupon.discount_amount)} off`,
          ar: `تم تطبيق القسيمة · خصم ${fmt(engineCoupon.discount_amount)}`,
        }),
        'success',
      );
      closeModal();
      return;
    }

    if (appliedCoupon) {
      setDiscount({
        type: appliedCoupon.discount_type,
        value: appliedCoupon.discount_value,
        reason: appliedCoupon.name,
        source: 'coupon',
        couponCode: couponInput.trim(),
        couponDId: appliedCoupon.coupon_d_id,
      });
      toast(t({ en: `Coupon applied · ${fmt(appliedCoupon.applies_to_amount)} off`, ar: `تم تطبيق القسيمة · خصم ${fmt(appliedCoupon.applies_to_amount)}` }), 'success');
      closeModal();
      return;
    }

    if (!valueIsValid) return;
    if (reasonRequired && !selectedReason) return;

    // Reason is optional. Use the picked reason's name when present; otherwise
    // synthesize a label for the receipt/cart — crediting the manager when an
    // override was involved, or a plain "Discount" when not. Either way we only
    // write DiscountReason_ID when a real reason was chosen.
    const reasonLabel = selectedReason?.name ?? (override
      ? t({ en: 'Manager-approved discount', ar: 'خصم بموافقة المدير' })
      : t({ en: 'Discount', ar: 'خصم' }));

    setDiscount({
      type: mode,
      // A percent value stays at 2 decimals; an amount is money and follows
      // the currency's precision (0.136 KWD must not snap to 0.14).
      value: mode === 'percent' ? round2(numericValue) : roundM(numericValue),
      reason: reasonLabel,
      source: 'manual',
      ...(selectedReason ? { discountReasonId: selectedReason.id } : {}),
      ...(override
        ? {
            approvedBy: override.managerUsername,
            managerOverrideUserId: override.managerUserId,
          }
        : {}),
    });
    toast(t({ en: `Discount applied · ${fmt(liveDiscountAmount)} off`, ar: `تم تطبيق الخصم · خصم ${fmt(liveDiscountAmount)}` }), 'success');
    closeModal();
  }

  function handleClear() {
    setDiscount(null);
    closeModal();
    toast(t({ en: 'Discount cleared', ar: 'تم مسح الخصم' }), 'info');
  }

  if (!isOpen) return null;

  // ── Render ──────────────────────────────────────────────────────────

  const PERCENT_CHIPS = [25, 40, 100];
  const AMOUNT_CHIPS = [10, 20, 50, 100, 200];

  return (
    <>
      <div className="modal-overlay show" onClick={closeModal}>
        <div
          className="modal"
          style={{ maxWidth: 480, width: '100%' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header">
            <div
              className="modal-header-img"
              style={{ background: 'var(--warning-light)', color: 'var(--warning)' }}
            >
              %
            </div>
            <div className="modal-header-title">
              <div className="modal-header-name">{t({ en: 'Apply discount', ar: 'إضافة خصم' })}</div>
              <div className="modal-header-name-ar">
                {fmt(subtotal)} · {totals.itemCount} {totals.itemCount === 1 ? t({ en: 'item', ar: 'صنف' }) : t({ en: 'items', ar: 'أصناف' })}
              </div>
            </div>
            <button className="modal-close" onClick={closeModal} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
              ×
            </button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Type toggle — disabled when a coupon is applied; absent without the tick */}
            {manualAllowed && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
                opacity: engineCoupon || appliedCoupon ? 0.5 : 1,
              }}
            >
              <button
                type="button"
                onClick={() => setMode('percent')}
                disabled={!!engineCoupon || !!appliedCoupon}
                style={toggleBtn(mode === 'percent', !!engineCoupon || !!appliedCoupon)}
              >
                <span>%</span>
                <span>{t({ en: 'Percent', ar: 'نسبة مئوية' })}</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('amount')}
                disabled={!!engineCoupon || !!appliedCoupon}
                style={toggleBtn(mode === 'amount', !!engineCoupon || !!appliedCoupon)}
              >
                <span>{currency.code}</span>
                <span>{t({ en: 'Amount', ar: 'مبلغ' })}</span>
              </button>
            </div>
            )}

            {appliedCoupon && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                  marginTop: -6,
                }}
              >
                {t({ en: 'Manual discount disabled while a coupon is applied', ar: 'الخصم اليدوي معطّل أثناء تطبيق قسيمة' })}
              </div>
            )}

            {!appliedCoupon && !manualAllowed && (
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--danger)',
                  textAlign: 'center',
                  padding: '10px 12px',
                  background: 'var(--danger-light, #fee2e2)',
                  borderRadius: 8,
                }}
              >
                {t({
                  en: 'You are not allowed to give an invoice discount. A coupon can still be applied.',
                  ar: 'ليس لديك صلاحية خصم على الفاتورة. يمكن تطبيق قسيمة فقط.',
                })}
              </div>
            )}

            {/* Manual entry — hidden behind coupon, and for a user without the tick */}
            {!appliedCoupon && manualAllowed && (
              <>
                <div>
                  <label style={labelStyle}>{t({ en: 'Discount value', ar: 'قيمة الخصم' })}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      // Percent steps by 0.01; an amount steps by one unit of
                      // the tenant's currency (0.001 on KWD).
                      step={mode === 'percent' ? '0.01' : amountStep()}
                      placeholder={mode === 'percent' ? '0' : amountText(0)}
                      value={valueStr}
                      onChange={(e) => setValueStr(e.target.value)}
                      style={bigInputStyle}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 18,
                        color: 'var(--text-muted)',
                        pointerEvents: 'none',
                      }}
                    >
                      {mode === 'percent' ? '%' : currency.code}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    // Column count tracks the chip count so the buttons stay
                    // edge-aligned with the input above instead of leaving
                    // empty cells when one mode has fewer chips than the other.
                    gridTemplateColumns: `repeat(${(mode === 'percent' ? PERCENT_CHIPS : AMOUNT_CHIPS).length}, 1fr)`,
                    gap: 8,
                  }}
                >
                  {(mode === 'percent' ? PERCENT_CHIPS : AMOUNT_CHIPS).map((v) => {
                    const active = valueIsValid && numericValue === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setValueStr(String(v))}
                        style={chipBtn(active)}
                      >
                        {mode === 'percent' ? `${v}%` : v}
                      </button>
                    );
                  })}
                </div>

                <div>
                  <label style={labelStyle}>
                    {t({ en: 'Reason', ar: 'السبب' })}{!reasonRequired && (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>
                        {' '}· {t({ en: 'optional', ar: 'اختياري' })}
                      </span>
                    )}
                  </label>
                  <select
                    value={reasonId ?? ''}
                    onChange={(e) =>
                      setReasonId(e.target.value ? parseInt(e.target.value, 10) : null)
                    }
                    style={selectStyle}
                  >
                    <option value="">{t({ en: '— Select a reason —', ar: '— اختر سببًا —' })}</option>
                    {reasons.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.requires_manager_override ? t({ en: ' (override required)', ar: ' (يتطلب موافقة المدير)' }) : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Override panel — appears inline when needed.
                  *
                  * The Authorize button is enabled as soon as the cashier
                  * has typed a value. We do NOT force them to pick a
                  * reason first — the manager's credentials ARE the
                  * authorization, and the reason (if required at all,
                  * see reasonRequired) can be chosen after.
                  */}
                {overrideNeeded && (
                  <div style={overridePanelStyle(!!override)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={overrideIconStyle(!!override)}>{override ? <CheckIcon size={14} /> : '⚠'}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {override
                            ? t({ en: `Authorized by ${override.managerUsername}`, ar: `تمت الموافقة من ${override.managerUsername}` })
                            : t({ en: 'Manager approval required', ar: 'موافقة المدير مطلوبة' })}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {override
                            ? t({ en: 'Tap Apply to save this discount', ar: 'اضغط تطبيق لحفظ هذا الخصم' })
                            : cashierMaxPerc === 0
                              ? t({ en: "You don't have permission — ask another user to enter their password", ar: 'ليس لديك صلاحية — اطلب من مستخدم آخر إدخال كلمة المرور' })
                              : t({ en: `${impliedPerc}% exceeds your ${cashierMaxPerc}% limit`, ar: `${impliedPerc}% يتجاوز حدك البالغ ${cashierMaxPerc}%` })}
                        </div>
                      </div>
                      {override ? (
                        <button
                          type="button"
                          onClick={() => setOverride(null)}
                          style={overrideClearBtnStyle}
                        >
                          {t({ en: 'Change', ar: 'تغيير' })}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleRequestOverride}
                          disabled={!valueIsValid}
                          style={overrideAuthBtnStyle(!valueIsValid)}
                          title={!valueIsValid ? t({ en: 'Enter a discount value first', ar: 'أدخل قيمة الخصم أولاً' }) : t({ en: 'Open the credentials prompt', ar: 'فتح نافذة بيانات الاعتماد' })}
                        >
                          {t({ en: 'Enter password', ar: 'أدخل كلمة المرور' })}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Coupon section */}
            <div
              style={{
                borderTop: '1px solid var(--border)',
                paddingTop: 16,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: 'var(--accent-light, #fbeaf0)',
                    color: 'var(--accent, #72243e)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  ★
                </div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {engineCoupon || appliedCoupon
                    ? t({ en: 'Coupon applied', ar: 'تم تطبيق القسيمة' })
                    : t({ en: 'Or apply a coupon code', ar: 'أو طبّق رمز قسيمة' })}
                </div>
              </div>

              {/* A coupon accepted by the engine. The AMOUNT shown is the
                  server's — it re-prices the same code at charge time. */}
              {engineCoupon ? (
                <div style={appliedCouponBoxStyle}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={appliedCouponBadgeStyle}>
                      <CheckIcon size={14} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                          {engineCoupon.code ?? couponInput.trim()}
                        </span>
                        <span style={appliedCouponTagStyle}>
                          {t({
                            en: `${fmt(engineCoupon.discount_amount)} ${currency.code} OFF`,
                            ar: `خصم ${fmt(engineCoupon.discount_amount)} ${currency.code}`,
                          })}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--success-text, #085041)' }}>
                        {engineCoupon.name}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      style={appliedCouponRemoveBtnStyle}
                      aria-label={t({ en: 'Remove coupon', ar: 'إزالة القسيمة' })}
                    >
                      <TrashIcon size={14} />
                      {t({ en: 'Remove', ar: 'إزالة' })}
                    </button>
                  </div>
                </div>
              ) : appliedCoupon ? (
                <div style={appliedCouponBoxStyle}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={appliedCouponBadgeStyle}>
                      <CheckIcon size={14} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {couponInput.trim()}
                        </span>
                        <span style={appliedCouponTagStyle}>
                          {appliedCoupon.discount_type === 'percent'
                            ? t({ en: `${appliedCoupon.discount_value}% OFF`, ar: `خصم ${appliedCoupon.discount_value}%` })
                            : t({ en: `${appliedCoupon.discount_value} ${currency.code} OFF`, ar: `خصم ${appliedCoupon.discount_value} ${currency.code}` })}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--success-text, #085041)', marginBottom: 4 }}>
                        {appliedCoupon.name}
                      </div>
                      {appliedCoupon.expires_at && (
                        <div style={{ fontSize: 11, color: 'var(--success-text, #0f6e56)' }}>
                          {appliedCoupon.is_all_items ? t({ en: 'Valid for all items', ar: 'صالحة لجميع الأصناف' }) : t({ en: 'Item restrictions apply', ar: 'تطبق قيود على الأصناف' })}
                          {t({ en: ' · expires ', ar: ' · تنتهي ' })}
                          {formatExpiry(appliedCoupon.expires_at)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      style={appliedCouponRemoveBtnStyle}
                      aria-label={t({ en: 'Remove coupon', ar: 'إزالة القسيمة' })}
                    >
                      <TrashIcon size={14} />
                      {t({ en: 'Remove', ar: 'إزالة' })}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                    <input
                      type="text"
                      placeholder={t({ en: 'Scan or type code…', ar: 'امسح أو اكتب الرمز…' })}
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleApplyCoupon();
                        }
                      }}
                      disabled={validatingCoupon}
                      style={{
                        ...selectStyle,
                        fontFamily: 'monospace',
                        ...(couponError
                          ? {
                              borderColor: 'var(--danger, #a32d2d)',
                              background: 'var(--danger-light, #fcebeb)',
                            }
                          : {}),
                      }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleApplyCoupon}
                      disabled={validatingCoupon || !couponInput.trim()}
                    >
                      {validatingCoupon ? '…' : t({ en: 'Apply', ar: 'تطبيق' })}
                    </button>
                  </div>
                  {couponError && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--danger, #791f1f)',
                        marginTop: 8,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 8,
                          background: 'var(--danger, #a32d2d)',
                          color: 'white',
                          fontSize: 10,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      >
                        !
                      </span>
                      <span>{couponError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Live preview */}
            <div style={previewBoxStyle}>
              <div style={previewRowStyle}>
                <span style={{ color: 'var(--text-muted)' }}>{t({ en: 'Subtotal', ar: 'المجموع الفرعي' })}</span>
                <span>{fmt(subtotal)}</span>
              </div>
              {liveDiscountAmount > 0 && (
                <div style={{ ...previewRowStyle, color: 'var(--danger)' }}>
                  <span>
                    {t({ en: 'Discount', ar: 'الخصم' })}
                    {appliedCoupon
                      ? ` (${appliedCoupon.discount_type === 'percent'
                          ? `${appliedCoupon.discount_value}%`
                          : 'coupon'})`
                      : mode === 'percent' && valueIsValid
                        ? ` (${numericValue}%)`
                        : ''}
                  </span>
                  <span>−{fmt(liveDiscountAmount)}</span>
                </div>
              )}
              <div
                style={{
                  ...previewRowStyle,
                  fontSize: 14,
                  fontWeight: 700,
                  paddingTop: 6,
                  borderTop: '1px solid var(--border)',
                  marginTop: 4,
                }}
              >
                <span>{t({ en: 'New total', ar: 'الإجمالي الجديد' })}</span>
                <span>{fmt(liveNewTotal)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{ gap: 8 }}>
            {existingDiscount && (
              <button type="button" className="btn-secondary" onClick={handleClear}>
                <TrashIcon size={14} />
                {t({ en: 'Remove', ar: 'إزالة' })}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn-secondary"
              onClick={closeModal}
              style={{ height: 'auto', padding: '11px 24px', fontSize: 14, fontWeight: 600 }}
            >
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button
              type="button"
              className="btn-primary-modal"
              onClick={handleApply}
              disabled={!canApply}
              style={!canApply ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              {t({ en: 'Apply discount', ar: 'تطبيق الخصم' })}
            </button>
          </div>
        </div>
      </div>

      {/* Child modal — opens above the discount modal when needed */}
      {overrideModalOpen && (
        <OverrideModal
          requestedDiscountPerc={Math.max(impliedPerc, 0.01)}
          onSuccess={handleOverrideSuccess}
          onClose={handleOverrideClose}
        />
      )}
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Round MONEY at the tenant currency's precision — 3 decimals on KWD, 2 on
 * EGP/SAR/AED. One rounding rule across the app (see store/cart.ts).
 */
function roundM(n: number): number {
  return roundMoney(n, moneyPrecision());
}

/**
 * Round a PERCENT. Always 2 decimals — a percentage isn't money and doesn't
 * follow the currency's precision (the override threshold is compared in
 * percent).
 */
function round2(n: number): number {
  return (Math.sign(n) * Math.round(Math.abs(n) * 100)) / 100;
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Inline styles (kept here so the modal is one self-contained file) ──

const labelStyle: import('react').CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const bigInputStyle: import('react').CSSProperties = {
  width: '100%',
  height: 56,
  padding: '0 50px 0 16px',
  fontSize: 28,
  fontWeight: 600,
  textAlign: 'center',
  border: '1px solid var(--border-strong, #d0d6de)',
  borderRadius: 'var(--radius, 10px)',
  background: 'var(--surface, #fff)',
  color: 'var(--text)',
  outline: 'none',
};

const selectStyle: import('react').CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  fontSize: 14,
  border: '1px solid var(--border, #e2e6ec)',
  borderRadius: 'var(--radius, 10px)',
  background: 'var(--surface, #fff)',
  color: 'var(--text)',
  outline: 'none',
};

const previewBoxStyle: import('react').CSSProperties = {
  background: 'var(--surface-2, #f3f5f8)',
  borderRadius: 'var(--radius, 10px)',
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
};

const previewRowStyle: import('react').CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
};

function toggleBtn(active: boolean, disabled: boolean): import('react').CSSProperties {
  return {
    padding: '12px 10px',
    borderRadius: 'var(--radius, 10px)',
    border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
    background: active ? 'var(--primary-light, #e6f1fb)' : 'var(--surface, #fff)',
    color: active ? 'var(--primary, #185fa5)' : 'var(--text-muted)',
    fontWeight: 600,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    lineHeight: 1.2,
  };
}

function chipBtn(active: boolean): import('react').CSSProperties {
  return {
    padding: '10px 0',
    fontSize: 14,
    borderRadius: 'var(--radius, 10px)',
    border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
    background: active ? 'var(--primary-light, #e6f1fb)' : 'var(--surface, #fff)',
    color: active ? 'var(--primary, #185fa5)' : 'var(--text)',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
  };
}

function overridePanelStyle(approved: boolean): import('react').CSSProperties {
  return {
    background: approved ? 'var(--success-light, #e1f5ee)' : 'var(--warning-light, #faeeda)',
    border: `1px solid ${approved ? 'var(--success, #1d9e75)' : 'var(--warning, #ba7517)'}`,
    borderRadius: 'var(--radius, 10px)',
    padding: 12,
  };
}

function overrideIconStyle(approved: boolean): import('react').CSSProperties {
  return {
    width: 28,
    height: 28,
    borderRadius: 6,
    background: approved ? 'var(--success, #1d9e75)' : 'var(--warning, #ba7517)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
  };
}

const overrideClearBtnStyle: import('react').CSSProperties = {
  padding: '6px 10px',
  fontSize: 12,
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius, 8px)',
  background: 'var(--surface)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};

function overrideAuthBtnStyle(disabled: boolean): import('react').CSSProperties {
  return {
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    borderRadius: 'var(--radius, 8px)',
    background: 'var(--warning, #ba7517)',
    color: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

const appliedCouponBoxStyle: import('react').CSSProperties = {
  background: 'var(--success-light, #e1f5ee)',
  border: '1px solid var(--success, #1d9e75)',
  borderRadius: 'var(--radius, 10px)',
  padding: 14,
};

const appliedCouponBadgeStyle: import('react').CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 6,
  background: 'var(--success, #1d9e75)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const appliedCouponTagStyle: import('react').CSSProperties = {
  fontSize: 10,
  padding: '2px 6px',
  background: 'var(--success-text, #04342c)',
  color: 'white',
  borderRadius: 3,
  fontWeight: 600,
};

const appliedCouponRemoveBtnStyle: import('react').CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  border: 'none',
  background: 'transparent',
  color: 'var(--danger, #993c1d)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
